import React, { useState, useContext } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { loader } from 'graphql.macro';
import { useMutation } from 'react-apollo-hooks';

import { ProfileContext } from '@loyalup/core';
import { WithTitle, Button, Form, Modal } from '@loyalup/ui';

import { ReactComponent as YclientsIcon } from 'icons/int/yclients.svg';
import { ReactComponent as YclientsGrayIcon } from 'icons/int/yclients_g.svg';
import { ReactComponent as CIcon } from 'icons/int/1c.svg';
import { ReactComponent as CGrayIcon } from 'icons/int/1c_g.svg';
import { ReactComponent as AtolIcon } from 'icons/int/atol.svg';
import { ReactComponent as AtolGrayIcon } from 'icons/int/atol_g.svg';
import { ReactComponent as BeautyProIcon } from 'icons/int/beautypro.svg';
import { ReactComponent as BeautyProGrayIcon } from 'icons/int/beautypro_g.svg';
import { ReactComponent as CloudshopIcon } from 'icons/int/cloudshop.svg';
import { ReactComponent as CloudshopGrayIcon } from 'icons/int/cloudshop_g.svg';
import { ReactComponent as EvotorIcon } from 'icons/int/evotor.svg';
import { ReactComponent as EvotorGrayIcon } from 'icons/int/evotor_g.svg';
import { ReactComponent as IikoIcon } from 'icons/int/iiko.svg';
import { ReactComponent as IikoGrayIcon } from 'icons/int/iiko_g.svg';
import { ReactComponent as PosterIcon } from 'icons/int/poster.svg';
import { ReactComponent as PosterGrayIcon } from 'icons/int/poster_g.svg';
import { ReactComponent as QuickRestoIcon } from 'icons/int/quickresto.svg';
import { ReactComponent as QuickRestoGrayIcon } from 'icons/int/quickresto_g.svg';
import { ReactComponent as RkeeperIcon } from 'icons/int/rkeeper.svg';
import { ReactComponent as RkeeperGrayIcon } from 'icons/int/rkeeper_g.svg';
import { ReactComponent as MoySkladIcon } from 'icons/int/moysklad.svg';
import { ReactComponent as MoySkladGreyIcon } from 'icons/int/moysklad_g.svg';

import subtotalImage from 'icons/int/subtotal.png';

import SetupYclients from './SetupYclients';
import SetupIIKO from './SetupIIKO';
import SetupSubtotal from './SetupSubtotal';
import SetupMoySklad from './SetupMoySklad';
import SetupPoster from './SetupPoster';

import s from './Integrations.module.css';

const TURN_OFF_YCLIENTS_MUTATION = loader('../../../gql/mutations/TurnOffYclients.gql');
const TURN_OFF_SUBTOTAL_MUTATION = loader('../../../gql/mutations/TurnOffSubtotal.gql');
const TURN_OFF_MOYSKLAD_MUTATION = loader('../../../gql/mutations/TurnOffMoySklad.gql');
const TURN_OFF_POSTER_MUTATION = loader('../../../gql/mutations/TurnOffPoster.gql');

const ChooseService = ({ onChange }) => {
  const [t] = useTranslation();
  return (
    <>
      <div className={cn(s.placeholder, s.big)}>{t('Нет активных интеграций с другими сервисами')}</div>
      <div className={s.placeholder}>{t('Для настройки интеграции выберете сервис')}</div>
      <div className={s.services}>
        <button className={s.serviceItem} onClick={() => onChange('YCLIENTS')}>
          <YclientsIcon className={s.icon} />
          <YclientsGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('IIKO')}>
          <IikoIcon className={s.icon} />
          <IikoGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('SUBTOTAL')}>
          <img src={subtotalImage} alt="" className={s.image} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('MOYSKLAD')}>
          <MoySkladIcon className={s.icon} />
          <MoySkladGreyIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('POSTER')}>
          <PosterIcon className={s.icon} />
          <PosterGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('BEAUTYPRO')} disabled>
          <BeautyProIcon className={s.icon} />
          <BeautyProGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('1C')} disabled>
          <CIcon className={s.icon} />
          <CGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('ATOL')} disabled>
          <AtolIcon className={s.icon} />
          <AtolGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('CLOUDSHOP')} disabled>
          <CloudshopIcon className={s.icon} />
          <CloudshopGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('EVOTOR')} disabled>
          <EvotorIcon className={s.icon} />
          <EvotorGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('QUICKRESTO')} disabled>
          <QuickRestoIcon className={s.icon} />
          <QuickRestoGrayIcon className={cn(s.icon, s.gray)} />
        </button>
        <button className={s.serviceItem} onClick={() => onChange('RKEEPER')} disabled>
          <RkeeperIcon className={s.icon} />
          <RkeeperGrayIcon className={cn(s.icon, s.gray)} />
        </button>
      </div>
    </>
  );
};

const ConnectedIntegration = ({ company, onDisconnect }) => {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [turnOffYclients] = useMutation(TURN_OFF_YCLIENTS_MUTATION);
  const [turnOffSubtotal] = useMutation(TURN_OFF_SUBTOTAL_MUTATION);
  const [turnOffMoySklad] = useMutation(TURN_OFF_MOYSKLAD_MUTATION);
  const [turnOffPoster] = useMutation(TURN_OFF_POSTER_MUTATION);

  const disconect = async () => {
    setLoading(true);
    let updatedCompany = company;
    if (company.integrationMode === 'SUBTOTAL') {
      const {
        data: { turnOffSubtotalIntegration },
      } = await turnOffSubtotal();
      updatedCompany = turnOffSubtotalIntegration;
    } else if (company.integrationMode === 'YCLIENTS') {
      const {
        data: { turnOffYclientsIntegration },
      } = await turnOffYclients();
      updatedCompany = turnOffYclientsIntegration;
    } else if (company.integrationMode === 'MOY_SKLAD') {
      const {
        data: { turnOffMoySkladIntegration },
      } = await turnOffMoySklad();
      updatedCompany = turnOffMoySkladIntegration;
    } else if (company.integrationMode === 'POSTER') {
      const {
        data: { turnOffPosterIntegration },
      } = await turnOffPoster();
      updatedCompany = turnOffPosterIntegration;
    }
    onDisconnect(updatedCompany);
  };

  const mode = {
    YCLIENTS: 'Y∙Clients',
    SUBTOTAL: 'Subtotal',
    MOY_SKLAD: t('МойСклад'),
    POSTER: t('Poster'),
  };

  return (
    <div className={s.connectedContainer}>
      {company.integrationMode === 'SUBTOTAL' && (
        <>
          <h2>{t('Установлена интеграция с Subtotal')}</h2>
          <div className={s.connectionInfo}>
            <div className={s.status}>
              {t('Логин subtotal:')}
              <b>{company.subtotalSettings.login}</b>
            </div>
            <div className={s.status}>
              {t('Статус интеграции:')}
              {['READY', 'OK'].includes(company.subtotalSettings.status) ? (
                <b className={s.ok}>{t('Активна')}</b>
              ) : (
                <b className={s.error}>{t('Ошибка подключения')}</b>
              )}
            </div>
          </div>
        </>
      )}
      {company.integrationMode === 'YCLIENTS' && (
        <>
          <h2>{t('Установлена интеграция с Y∙Clients')}</h2>
          <div className={s.connectionInfo}>
            <div className={s.status}>
              {t('Логин yclients:')}
              <b>{company.yclientsSettings.login}</b>
            </div>
            <div className={s.status}>
              {t('Статус интеграции:')}
              {company.yclientsSettings.status === 'OK' ? (
                <b className={s.ok}>{t('Активна')}</b>
              ) : (
                <b className={s.error}>{t('Ошибка подключения')}</b>
              )}
            </div>
          </div>
        </>
      )}
      {company.integrationMode === 'MOY_SKLAD' && (
        <>
          <h2>{t('Установлена интеграция с МойСклад')}</h2>
          <div className={s.connectionInfo}>
            <div className={s.status}>
              {t('Логин МойСклад:')}
              <b>{company.moySkladSettings.login}</b>
            </div>
            <div className={s.status}>
              {t('Статус интеграции:')}
              {company.moySkladSettings.status === 'OK' ? (
                <b className={s.ok}>{t('Активна')}</b>
              ) : (
                <b className={s.error}>{t('Ошибка подключения')}</b>
              )}
            </div>
          </div>
        </>
      )}
      {company.integrationMode === 'POSTER' && (
        <>
          <h2>{t('Установлена интеграция с Poster')}</h2>
          <div className={s.connectionInfo}>
            <div className={s.status}>
              {t('Статус интеграции:')}
              {['READY', 'OK'].includes(company.posterSettings.status) ? (
                <b className={s.ok}>{t('Активна')}</b>
              ) : (
                <b className={s.error}>{t('Ошибка подключения')}</b>
              )}
            </div>
          </div>
        </>
      )}
      {company.integrationMode !== 'NOTHING' && (
        <>
          <Button theme="black" loading={loading} onClick={() => setShowConfirm(true)}>
            {t('Отключить')}
          </Button>
          <Modal confirm isOpen={showConfirm} onClose={() => setShowConfirm(false)}>
            <Form
              onSubmit={() => {
                setShowConfirm(false);
                disconect();
              }}
            >
              <p>
                {t('Вы уверены что хотите отключить интеграцию с {{mode}}?', {
                  mode: mode[company.integrationMode],
                })}
              </p>
              <div>
                <Button type="submit" theme="green" center>
                  {t('Отключить')}
                </Button>
                <Button theme="black" onClick={() => setShowConfirm(false)}>
                  {t('Отмена')}
                </Button>
              </div>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
};

const SetupIntegration = ({ onFinish }) => {
  const [mode, setMode] = useState();

  if (mode === 'YCLIENTS') {
    return <SetupYclients onFinish={onFinish} />;
  }
  if (mode === 'IIKO') {
    return <SetupIIKO />;
  }
  if (mode === 'SUBTOTAL') {
    return <SetupSubtotal />;
  }
  if (mode === 'MOYSKLAD') {
    return <SetupMoySklad />;
  }
  if (mode === 'POSTER') {
    return <SetupPoster />;
  }

  return <ChooseService onChange={setMode} />;
};

const Integrations = () => {
  const [t] = useTranslation();
  const { company: defaultCompany } = useContext(ProfileContext);
  const [company, setCompany] = useState(defaultCompany);

  return (
    <WithTitle title={t('Настройки интеграций')}>
      <div className={s.header}>
        <h1>{t('Настройки интеграций')}</h1>
      </div>
      <div className={s.centered}>
        {company.integrationMode === 'NOTHING' ? (
          <SetupIntegration />
        ) : (
          <ConnectedIntegration company={company} onDisconnect={setCompany} />
        )}
      </div>
    </WithTitle>
  );
};

export default Integrations;
